import getConfig from "../config";
import type { Feature } from "./types";
import { useInternalUserValidation, useIsEligible } from "./useAccessControl";
import { useSettingsValidation } from "./useSettingsValidation";

const config = getConfig();
const appEnv = config.appEnv;

/**
 * Custom hook to determine if a feature should be enabled based on permissions and settings
 * @param {Feature} feature - Object containing permission and setting properties
 * @param {boolean} [viewOnly=false] - Flag to indicate if the feature is for view-only access
 * @returns {boolean} - Returns true if the user has permission and the feature is enabled in settings
 * @example
 * const feature = {
 *   permission: "CAN_USE_FEATURE_X",
 *   setting: "FEATURE_X_ENABLED"
 * };
 * const isAuthorized = useIsAuthorized(feature);
 */
export const useIsAuthorized = (feature: Feature, viewOnly: boolean = false): boolean => {
    // Handle setting property - exists in FeatureFlagConfig but not AccessConfig
    const setting: string = "setting" in feature ? (feature.setting ?? "") : "";

    // Handle internalUser property - exists in both types
    const internalUser = feature.internalUser ?? false;

    const isSettingEnabled = useSettingsValidation(setting);

    const isInternalUserEnabled = useInternalUserValidation(internalUser);

    // Check if feature should be hidden in current environment
    const isHiddenEnv = feature.env ? !feature.env.includes(appEnv as string) : false;

    const isEligible = useIsEligible(feature, viewOnly);

    return !isHiddenEnv && isSettingEnabled && isInternalUserEnabled && isEligible;
};
